/* eslint-disable */
import React, { useRef, useState, useEffect, useMemo, MutableRefObject } from 'react';
import { observer } from 'mobx-react-lite';
import { SelectMultipleState } from './SelectMultiple.state';
import { FormInputState, FormModel } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    SelectWrapper,
    SelectElement,
    SelectError,
    Values,
    Value,
    Options,
    Option,
    CloseIconWrapper,
    ChevronDownWrapper,
    CheckboxWrapper,
    CheckIconWrapper,
} from './SelectMultiple.style';

const useClickOutside = (
    ref: MutableRefObject<unknown>,
    targetToIgnore: MutableRefObject<unknown>,
    onClickOutsideCallback: () => void
): void => {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            // @ts-expect-error
            if (ref.current && !ref.current.contains(event.target) && event.target !== targetToIgnore.current) {
                onClickOutsideCallback();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return (): void => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
};

export interface SelectOptionsType {
    [key: string]: string;
}

interface SelectPropsType {
    className?: string;
    name: string;
    options: SelectOptionsType;
    minSelections: number;
    maxSelections: number;
    placeholder: string;
    model: FormModel<Array<string>>;
    state: FormInputState<Array<string>>;
    status?: InputStatus;
    viewType?: 'default' | 'small';
}

export const SelectMultiple = observer(
    ({
        className,
        name,
        options,
        state,
        status,
        model,
        maxSelections,
        viewType,
        placeholder = '',
    }: SelectPropsType): JSX.Element => {
        const { type: statusType, message: statusMessage } = status || {};

        const optionsArray = useMemo(() => Object.keys(options), [options]);
        const [selectState] = useState(() => new SelectMultipleState(state, model, maxSelections));
        const valuesRef = useRef(null);
        const optionsRef = useRef(null);
        useClickOutside(optionsRef, valuesRef, selectState.hideOptions);

        const renderStatusMessage = (): JSX.Element | null => {
            if (statusType === 'error' && statusMessage !== undefined && statusMessage !== null) {
                return <SelectError viewType={viewType}>{statusMessage}</SelectError>;
            }
            return null;
        };

        return (
            <SelectWrapper
                className={className}
                viewType={viewType}
            >
                <Values
                    ref={valuesRef}
                    onClick={selectState.toggleOptionsVisibility}
                    viewType={viewType}
                >
                    {selectState.value.length === 0
                        ? placeholder
                        : selectState.value.map((value) => {
                              if (options[value] !== undefined) {
                                  return (
                                      <Value
                                          key={value}
                                          viewType={viewType}
                                      >
                                          {options[value]}
                                          <CloseIconWrapper
                                              onClick={(event): void => {
                                                  // @ts-expect-error
                                                  event.stopPropagation();
                                                  selectState.hideOptions();
                                                  selectState.removeValue(value);
                                              }}
                                          >
                                              &#215;
                                          </CloseIconWrapper>
                                      </Value>
                                  );
                              }
                              return null;
                          })}
                    <ChevronDownWrapper />
                </Values>
                <Options
                    ref={optionsRef}
                    hidden={!selectState.areOptionsVisible}
                    viewType={viewType}
                >
                    {optionsArray.map((value: string) => {
                        //@ts-expect-error
                        const optionLabel: string = options[value];
                        const isSelected = Boolean(selectState.value.find((selectedValue) => selectedValue === value));

                        return (
                            <Option
                                key={value}
                                viewType={viewType}
                                onClick={(): void => {
                                    // eslint-disable-next-line no-unused-expressions
                                    isSelected ? selectState.removeValue(value) : selectState.addValue(value);
                                }}
                                isSelected={isSelected}
                            >
                                <span>{optionLabel}</span>
                                {viewType === 'small' ? (
                                    <CheckboxWrapper isChecked={isSelected}>
                                        {isSelected ? <CheckIconWrapper isChecked={isSelected} /> : null}
                                    </CheckboxWrapper>
                                ) : null}
                            </Option>
                        );
                    })}
                </Options>
                <SelectElement
                    name={name}
                    value={selectState.value}
                    onFocus={selectState.handleFocus}
                    onBlur={selectState.handleBlur}
                    onChange={() => {}}
                    multiple
                    hidden
                    viewType={viewType}
                >
                    {optionsArray.map((value: string) => {
                        //@ts-expect-error
                        const optionLabel: string = options[value];
                        return (
                            <option
                                key={value}
                                value={value}
                            >
                                {optionLabel}
                            </option>
                        );
                    })}
                </SelectElement>
                {renderStatusMessage()}
            </SelectWrapper>
        );
    }
);
